// src/pages/Warehouse/ProductTypes/ProductTypesTable.jsx
import React, { useEffect, useMemo, useState } from "react"
import { Table, Tag, Space, Typography, Input, Button, Popconfirm, message } from "antd"
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import dayjs from "dayjs"
import AddProductTypeDrawer from "./AddProductTypeDrawer"

const { Text } = Typography
const { Search } = Input

export default function ProductTypesTable() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [qText, setQText] = useState("")
  const [editing, setEditing] = useState(null) // { id, name, description, fields }
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    const ref = collection(db, "warehouseProductTypes")
    const qy = query(ref, orderBy("createdAt", "desc"))
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            name: data.name ?? "",
            description: data.description ?? "",
            fields: Array.isArray(data.fields) ? data.fields : [],
            createdAt: data.createdAt?.toDate?.() ?? null,
          }
        })
        setRows(list)
        setLoading(false)
      },
      () => setLoading(false)
    )
    return () => unsub()
  }, [])

  const filtered = useMemo(() => {
    const term = qText.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((r) => {
      const inName = r.name.toLowerCase().includes(term)
      const inDesc = r.description.toLowerCase().includes(term)
      const inFields =
        r.fields?.some?.(
          (f) =>
            f.label?.toLowerCase?.().includes(term) ||
            f.key?.toLowerCase?.().includes(term) ||
            f.type?.toLowerCase?.().includes(term)
        ) ?? false
      return inName || inDesc || inFields
    })
  }, [rows, qText])

  const onEdit = (record) => {
    setEditing(record)
    setDrawerOpen(true)
  }

  const onDelete = async (record) => {
    try {
      await deleteDoc(doc(db, "warehouseProductTypes", record.id))
      message.success("Product type deleted.")
    } catch (e) {
      console.error(e)
      message.error("Failed to delete product type.")
    }
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v) => <Text type="secondary">{v}</Text>,
    },
    {
      title: "Fields",
      dataIndex: "fields",
      key: "fields",
      render: (fields = []) => {
        const req = fields.filter((f) => f.required).length
        const opt = fields.length - req
        return (
          <Space size="small" wrap>
            <Tag color="geekblue">required: {req}</Tag>
            <Tag>optional: {opt}</Tag>
            {fields.slice(0, 4).map((f) => (
              <Tag key={f.key} color={f.required ? "red" : "default"}>
                {f.label || f.key}
              </Tag>
            ))}
            {fields.length > 4 && <Tag>+{fields.length - 4} more</Tag>}
          </Space>
        )
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD HH:mm") : <Text type="secondary">—</Text>),
      sorter: (a, b) => {
        const ta = a.createdAt ? a.createdAt.getTime() : 0
        const tb = b.createdAt ? b.createdAt.getTime() : 0
        return ta - tb
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete product type?"
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(record)}>
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ margin: "8px 0 12px" }}>
        <Search
          allowClear
          placeholder="Search by name, description, or field…"
          onChange={(e) => setQText(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      <Table
        size="small"
        rowKey={(r) => r.id}
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: 8 }}>
              <Text strong>Schema</Text>
              <Table
                style={{ marginTop: 8 }}
                size="small"
                rowKey={(f) => f.key}
                pagination={false}
                dataSource={record.fields}
                columns={[
                  { title: "Label", dataIndex: "label", key: "label" },
                  { title: "Key", dataIndex: "key", key: "key" },
                  { title: "Type", dataIndex: "type", key: "type", width: 120 },
                  {
                    title: "Required",
                    dataIndex: "required",
                    key: "required",
                    width: 100,
                    render: (req) => (req ? <Tag color="red">yes</Tag> : <Tag>no</Tag>),
                  },
                ]}
              />
            </div>
          ),
        }}
      />

      {/* Controlled drawer for editing */}
      {drawerOpen && (
        <AddProductTypeDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false)
            setEditing(null)
          }}
          initialDoc={editing}
        />
      )}
    </div>
  )
}
